import React, { useState, useCallback } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import {
  Calculator,
  DollarSign,
  Home,
  TrendingUp,
  PiggyBank,
  ChevronDown,
  ArrowRight,
  Info,
} from 'lucide-react';

/* ───────────────────────── helpers ───────────────────────── */

const fmt = (v) =>
  v == null || isNaN(v)
    ? '$0'
    : new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(v);

const fmtInput = (v) => {
  const n = String(v).replace(/[^0-9]/g, '');
  return n ? Number(n).toLocaleString('en-AU') : '';
};

const parse = (v) => Number(String(v).replace(/[^0-9.]/g, '')) || 0;

/* ───────────────────────── reusable UI atoms ───────────────────────── */

const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700 mb-1">
    {children}
  </label>
);

const CurrencyInput = ({ id, label, value, onChange, placeholder }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        className="w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#99DF63] focus:border-transparent transition"
        value={fmtInput(value)}
        onChange={(e) => onChange(parse(e.target.value))}
        placeholder={placeholder}
      />
    </div>
  </div>
);

const NumberInput = ({ id, label, value, onChange, suffix, step = '0.1' }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <div className="relative">
      <input
        id={id}
        type="number"
        step={step}
        className="w-full pr-8 pl-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#99DF63] focus:border-transparent transition"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
          {suffix}
        </span>
      )}
    </div>
  </div>
);

const Select = ({ id, label, value, onChange, options }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <div className="relative">
      <select
        id={id}
        className="w-full appearance-none pl-3 pr-8 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#99DF63] focus:border-transparent transition bg-white"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
    </div>
  </div>
);

const Toggle = ({ options, value, onChange }) => (
  <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        onClick={() => onChange(o.value)}
        className={`px-4 py-2 text-sm font-medium transition ${
          value === o.value
            ? 'bg-[#00594C] text-white'
            : 'bg-white text-gray-600 hover:bg-gray-50'
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const CalcButton = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full sm:w-auto px-8 py-3 bg-[#99DF63] text-[#00594C] rounded-full font-bold text-sm hover:brightness-105 active:scale-[0.98] transition"
  >
    {children}
  </button>
);

const ResultBox = ({ children }) => (
  <div className="mt-6 p-5 bg-[#00594C]/5 border-l-4 border-[#99DF63] rounded-r-lg">
    {children}
  </div>
);

/* ═══════════════════════════════════════════════════════════
   CALCULATOR 1 — Borrowing Power
   ═══════════════════════════════════════════════════════════ */

const BorrowingPower = () => {
  const [income, setIncome] = useState('');
  const [partnerIncome, setPartnerIncome] = useState('');
  const [expenses, setExpenses] = useState('');
  const [existingRepayments, setExistingRepayments] = useState(0);
  const [dependants, setDependants] = useState('0');
  const [rate, setRate] = useState('6.5');
  const [term, setTerm] = useState('30');
  const [result, setResult] = useState(null);

  const calculate = useCallback(() => {
    const grossMonthly = (parse(income) + parse(partnerIncome)) / 12;
    const depCost = Number(dependants) * 400;
    const commitments = parse(expenses) + parse(existingRepayments) + depCost;
    const available = grossMonthly - commitments;
    const maxRepayment = available * 0.8;

    if (maxRepayment <= 0) {
      setResult({ maxLoan: 0, maxRepayment: 0, commitments, available });
      return;
    }

    const assessmentRate = (parseFloat(rate) + 3) / 100 / 12;
    const n = Number(term) * 12;
    const pv = maxRepayment * ((1 - Math.pow(1 + assessmentRate, -n)) / assessmentRate);

    setResult({
      maxLoan: Math.max(0, Math.round(pv)),
      maxRepayment: Math.round(maxRepayment),
      commitments: Math.round(commitments),
      available: Math.round(available),
    });
  }, [income, partnerIncome, expenses, existingRepayments, dependants, rate, term]);

  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <CurrencyInput id="bp-income" label="Annual gross income" value={income} onChange={setIncome} placeholder="120,000" />
        <CurrencyInput id="bp-partner" label="Partner's annual income (optional)" value={partnerIncome} onChange={setPartnerIncome} placeholder="0" />
        <CurrencyInput id="bp-expenses" label="Monthly living expenses" value={expenses} onChange={setExpenses} placeholder="3,000" />
        <CurrencyInput id="bp-repayments" label="Existing loan repayments / month" value={existingRepayments} onChange={setExistingRepayments} placeholder="0" />
        <Select
          id="bp-dep"
          label="Number of dependants"
          value={dependants}
          onChange={setDependants}
          options={[0, 1, 2, 3, 4, 5].map((d) => ({ value: String(d), label: String(d) }))}
        />
        <NumberInput id="bp-rate" label="Interest rate" value={rate} onChange={setRate} suffix="%" />
        <Select
          id="bp-term"
          label="Loan term"
          value={term}
          onChange={setTerm}
          options={[
            { value: '25', label: '25 years' },
            { value: '30', label: '30 years' },
          ]}
        />
      </div>
      <CalcButton onClick={calculate}>Calculate Borrowing Power</CalcButton>

      {result && (
        <ResultBox>
          <p className="text-sm text-gray-500 mb-1">Estimated maximum borrowing amount</p>
          <p className="text-3xl font-bold text-[#00594C]">{fmt(result.maxLoan)}</p>
          <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
            <div className="bg-white rounded-lg p-3">
              <p className="text-gray-500">Max monthly repayment</p>
              <p className="font-semibold text-gray-800">{fmt(result.maxRepayment)}</p>
            </div>
            <div className="bg-white rounded-lg p-3">
              <p className="text-gray-500">Monthly commitments</p>
              <p className="font-semibold text-gray-800">{fmt(result.commitments)}</p>
            </div>
            <div className="bg-white rounded-lg p-3">
              <p className="text-gray-500">Available monthly</p>
              <p className="font-semibold text-gray-800">{fmt(result.available)}</p>
            </div>
          </div>
          <p className="mt-3 text-xs text-gray-400 flex items-start gap-1">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            Assessment rate includes a 3% serviceability buffer as required by APRA guidelines.
          </p>
        </ResultBox>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   CALCULATOR 2 — Repayment Calculator
   ═══════════════════════════════════════════════════════════ */

const RepaymentCalc = () => {
  const [loanAmount, setLoanAmount] = useState('');
  const [rate, setRate] = useState('6.5');
  const [term, setTerm] = useState('30');
  const [frequency, setFrequency] = useState('monthly');
  const [result, setResult] = useState(null);

  const calculate = useCallback(() => {
    const P = parse(loanAmount);
    const r = parseFloat(rate) / 100 / 12;
    const n = Number(term) * 12;

    if (P <= 0 || r <= 0) return;

    const monthly = P * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const totalCost = monthly * n;
    const totalInterest = totalCost - P;

    let repayment = monthly;
    let freqLabel = 'month';
    if (frequency === 'fortnightly') {
      repayment = monthly / 2;
      freqLabel = 'fortnight';
    } else if (frequency === 'weekly') {
      repayment = monthly / 4;
      freqLabel = 'week';
    }

    setResult({
      repayment: Math.round(repayment),
      totalInterest: Math.round(totalInterest),
      totalCost: Math.round(totalCost),
      freqLabel,
    });
  }, [loanAmount, rate, term, frequency]);

  const termOptions = [5, 10, 15, 20, 25, 30].map((y) => ({
    value: String(y),
    label: `${y} years`,
  }));

  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <CurrencyInput id="rc-amount" label="Loan amount" value={loanAmount} onChange={setLoanAmount} placeholder="500,000" />
        <NumberInput id="rc-rate" label="Interest rate" value={rate} onChange={setRate} suffix="%" />
        <Select id="rc-term" label="Loan term" value={term} onChange={setTerm} options={termOptions} />
        <div>
          <Label>Repayment frequency</Label>
          <Toggle
            options={[
              { value: 'monthly', label: 'Monthly' },
              { value: 'fortnightly', label: 'Fortnightly' },
              { value: 'weekly', label: 'Weekly' },
            ]}
            value={frequency}
            onChange={setFrequency}
          />
        </div>
      </div>
      <CalcButton onClick={calculate}>Calculate Repayments</CalcButton>

      {result && (
        <ResultBox>
          <p className="text-sm text-gray-500 mb-1">Your estimated repayment per {result.freqLabel}</p>
          <p className="text-3xl font-bold text-[#00594C]">{fmt(result.repayment)}</p>
          <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
            <div className="bg-white rounded-lg p-3">
              <p className="text-gray-500">Total interest paid</p>
              <p className="font-semibold text-gray-800">{fmt(result.totalInterest)}</p>
            </div>
            <div className="bg-white rounded-lg p-3">
              <p className="text-gray-500">Total cost of loan</p>
              <p className="font-semibold text-gray-800">{fmt(result.totalCost)}</p>
            </div>
          </div>
        </ResultBox>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   CALCULATOR 3 — Stamp Duty Calculator
   ═══════════════════════════════════════════════════════════ */

const stampDutyBrackets = {
  NSW: [
    { min: 0, max: 16000, base: 0, rate: 0.0125 },
    { min: 16000, max: 35000, base: 200, rate: 0.015 },
    { min: 35000, max: 93000, base: 485, rate: 0.0175 },
    { min: 93000, max: 351000, base: 1500, rate: 0.035 },
    { min: 351000, max: 1168000, base: 10500, rate: 0.045 },
    { min: 1168000, max: Infinity, base: 47295, rate: 0.055 },
  ],
  VIC: [
    { min: 0, max: 25000, base: 0, rate: 0.014 },
    { min: 25000, max: 130000, base: 350, rate: 0.024 },
    { min: 130000, max: 960000, base: 2870, rate: 0.06 },
    { min: 960000, max: 2000000, base: 52670, rate: 0.055 },
    { min: 2000000, max: Infinity, base: 110000, rate: 0.065 },
  ],
  QLD: [
    { min: 0, max: 5000, base: 0, rate: 0.0 },
    { min: 5000, max: 75000, base: 0, rate: 0.015 },
    { min: 75000, max: 540000, base: 1050, rate: 0.035 },
    { min: 540000, max: 1000000, base: 17325, rate: 0.045 },
    { min: 1000000, max: Infinity, base: 38025, rate: 0.0575 },
  ],
  WA: [
    { min: 0, max: 120000, base: 0, rate: 0.019 },
    { min: 120000, max: 150000, base: 2280, rate: 0.0285 },
    { min: 150000, max: 360000, base: 3135, rate: 0.038 },
    { min: 360000, max: 725000, base: 11115, rate: 0.0475 },
    { min: 725000, max: Infinity, base: 28453, rate: 0.0515 },
  ],
  SA: [
    { min: 0, max: 12000, base: 0, rate: 0.01 },
    { min: 12000, max: 30000, base: 120, rate: 0.02 },
    { min: 30000, max: 50000, base: 480, rate: 0.03 },
    { min: 50000, max: 100000, base: 1080, rate: 0.035 },
    { min: 100000, max: 200000, base: 2830, rate: 0.04 },
    { min: 200000, max: 250000, base: 6830, rate: 0.042 },
    { min: 250000, max: 300000, base: 8930, rate: 0.046 },
    { min: 300000, max: 500000, base: 11230, rate: 0.05 },
    { min: 500000, max: Infinity, base: 21330, rate: 0.055 },
  ],
  TAS: [
    { min: 0, max: 3000, base: 50, rate: 0.0 },
    { min: 3000, max: 25000, base: 50, rate: 0.0175 },
    { min: 25000, max: 75000, base: 435, rate: 0.025 },
    { min: 75000, max: 200000, base: 1685, rate: 0.035 },
    { min: 200000, max: 375000, base: 6060, rate: 0.04 },
    { min: 375000, max: 725000, base: 13060, rate: 0.0425 },
    { min: 725000, max: Infinity, base: 27935, rate: 0.045 },
  ],
  NT: [
    { min: 0, max: 525000, base: 0, rate: 0.0 },
    { min: 525000, max: Infinity, base: 0, rate: 0.0495 },
  ],
  ACT: [
    { min: 0, max: 200000, base: 0, rate: 0.012 },
    { min: 200000, max: 300000, base: 2400, rate: 0.022 },
    { min: 300000, max: 500000, base: 4600, rate: 0.034 },
    { min: 500000, max: 750000, base: 11400, rate: 0.041 },
    { min: 750000, max: 1000000, base: 21650, rate: 0.0485 },
    { min: 1000000, max: 1455000, base: 33775, rate: 0.054 },
    { min: 1455000, max: Infinity, base: 58345, rate: 0.067 },
  ],
};

const calcStampDuty = (price, state) => {
  const brackets = stampDutyBrackets[state];
  if (!brackets) return 0;
  for (let i = brackets.length - 1; i >= 0; i--) {
    const b = brackets[i];
    if (price > b.min) {
      return Math.round(b.base + (price - b.min) * b.rate);
    }
  }
  return 0;
};

const getFirstHomeConcession = (price, state) => {
  switch (state) {
    case 'NSW':
      if (price <= 800000) return 'full';
      if (price <= 1000000) return 'partial';
      return 'none';
    case 'VIC':
      if (price <= 600000) return 'full';
      if (price <= 750000) return 'partial';
      return 'none';
    case 'QLD':
      if (price <= 550000) return 'full';
      if (price <= 700000) return 'partial';
      return 'none';
    case 'ACT':
      if (price <= 607500) return 'full';
      return 'none';
    case 'SA':
      if (price <= 650000) return 'partial';
      return 'none';
    case 'WA':
      if (price <= 430000) return 'full';
      if (price <= 530000) return 'partial';
      return 'none';
    case 'TAS':
      if (price <= 400000) return 'full';
      return 'none';
    case 'NT':
      if (price <= 650000) return 'full';
      return 'none';
    default:
      return 'none';
  }
};

const StampDutyCalc = () => {
  const [price, setPrice] = useState('');
  const [state, setState] = useState('NSW');
  const [propertyType, setPropertyType] = useState('established');
  const [buyerType, setBuyerType] = useState('first');
  const [result, setResult] = useState(null);

  const calculate = useCallback(() => {
    const p = parse(price);
    if (p <= 0) return;

    let duty = calcStampDuty(p, state);
    let concession = 'none';
    let concessionText = '';

    if (buyerType === 'first') {
      concession = getFirstHomeConcession(p, state);
      if (concession === 'full') {
        concessionText = 'First home buyer exemption applied';
        duty = 0;
      } else if (concession === 'partial') {
        concessionText = 'Partial first home buyer concession applied';
        duty = Math.round(duty * 0.5);
      }
    }

    setResult({
      duty,
      concessionText,
      totalCost: p + duty,
    });
  }, [price, state, buyerType]);

  const stateOptions = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'NT', 'ACT'].map((s) => ({
    value: s,
    label: s,
  }));

  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <CurrencyInput id="sd-price" label="Property value" value={price} onChange={setPrice} placeholder="750,000" />
        <Select id="sd-state" label="State" value={state} onChange={setState} options={stateOptions} />
        <div>
          <Label>Property type</Label>
          <Toggle
            options={[
              { value: 'established', label: 'Established' },
              { value: 'new', label: 'New Build' },
            ]}
            value={propertyType}
            onChange={setPropertyType}
          />
        </div>
        <div>
          <Label>Buyer type</Label>
          <Toggle
            options={[
              { value: 'first', label: 'First Home Buyer' },
              { value: 'existing', label: 'Existing Owner' },
              { value: 'investor', label: 'Investor' },
            ]}
            value={buyerType}
            onChange={setBuyerType}
          />
        </div>
      </div>
      <CalcButton onClick={calculate}>Calculate Stamp Duty</CalcButton>

      {result && (
        <ResultBox>
          <p className="text-sm text-gray-500 mb-1">Estimated stamp duty</p>
          <p className="text-3xl font-bold text-[#00594C]">{fmt(result.duty)}</p>
          {result.concessionText && (
            <p className="mt-2 text-sm font-medium text-green-700 bg-green-50 inline-block px-3 py-1 rounded-full">
              {result.concessionText}
            </p>
          )}
          <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
            <div className="bg-white rounded-lg p-3">
              <p className="text-gray-500">Property value</p>
              <p className="font-semibold text-gray-800">{fmt(parse(price))}</p>
            </div>
            <div className="bg-white rounded-lg p-3">
              <p className="text-gray-500">Total purchase cost</p>
              <p className="font-semibold text-gray-800">{fmt(result.totalCost)}</p>
            </div>
          </div>
          <p className="mt-3 text-xs text-gray-400 flex items-start gap-1">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            This is an estimate only. Contact us for exact figures.
          </p>
        </ResultBox>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   CALCULATOR 4 — Extra Repayment Calculator
   ═══════════════════════════════════════════════════════════ */

const ExtraRepaymentCalc = () => {
  const [balance, setBalance] = useState('');
  const [rate, setRate] = useState('6.5');
  const [term, setTerm] = useState('25');
  const [extra, setExtra] = useState('');
  const [result, setResult] = useState(null);

  const calculate = useCallback(() => {
    const P = parse(balance);
    const r = parseFloat(rate) / 100 / 12;
    const n = Number(term) * 12;
    const extraAmt = parse(extra);

    if (P <= 0 || r <= 0 || extraAmt <= 0) return;

    const monthlyPmt = P * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const totalStandard = monthlyPmt * n;
    const interestStandard = totalStandard - P;

    // Simulate extra repayment schedule
    let bal = P;
    let months = 0;
    let totalPaidExtra = 0;
    const pmt = monthlyPmt + extraAmt;

    while (bal > 0 && months < n) {
      const interestThisMonth = bal * r;
      const principalThisMonth = Math.min(bal, pmt - interestThisMonth);
      bal -= principalThisMonth;
      totalPaidExtra += interestThisMonth + principalThisMonth;
      months++;
      if (bal < 1) {
        bal = 0;
        break;
      }
    }

    const interestExtra = totalPaidExtra - P;
    const monthsSaved = n - months;
    const yearsSaved = Math.floor(monthsSaved / 12);
    const remainingMonths = monthsSaved % 12;
    const interestSaved = interestStandard - interestExtra;

    const today = new Date();
    const payoffDate = new Date(today);
    payoffDate.setMonth(payoffDate.getMonth() + months);
    const payoffStr = payoffDate.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });

    setResult({
      yearsSaved,
      monthsSaved: remainingMonths,
      interestSaved: Math.round(interestSaved),
      newPayoff: payoffStr,
      originalPayoff: (() => {
        const d = new Date(today);
        d.setMonth(d.getMonth() + n);
        return d.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
      })(),
    });
  }, [balance, rate, term, extra]);

  const termOptions = [];
  for (let y = 5; y <= 30; y++) {
    termOptions.push({ value: String(y), label: `${y} years` });
  }

  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <CurrencyInput id="er-balance" label="Current loan balance" value={balance} onChange={setBalance} placeholder="450,000" />
        <NumberInput id="er-rate" label="Interest rate" value={rate} onChange={setRate} suffix="%" />
        <Select id="er-term" label="Remaining loan term" value={term} onChange={setTerm} options={termOptions} />
        <CurrencyInput id="er-extra" label="Extra repayment per month" value={extra} onChange={setExtra} placeholder="500" />
      </div>
      <CalcButton onClick={calculate}>Calculate Savings</CalcButton>

      {result && (
        <ResultBox>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">Time saved</p>
              <p className="text-2xl font-bold text-[#00594C]">
                {result.yearsSaved > 0 && `${result.yearsSaved} yr${result.yearsSaved !== 1 ? 's' : ''} `}
                {result.monthsSaved} mo{result.monthsSaved !== 1 ? 's' : ''}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Interest saved</p>
              <p className="text-2xl font-bold text-[#00594C]">{fmt(result.interestSaved)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">New payoff date</p>
              <p className="text-2xl font-bold text-[#00594C]">{result.newPayoff}</p>
            </div>
          </div>
          <p className="mt-3 text-xs text-gray-400">
            Original payoff: {result.originalPayoff}
          </p>
        </ResultBox>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   TAB DATA & MAIN PAGE
   ═══════════════════════════════════════════════════════════ */

const tabs = [
  { id: 'borrowing', label: 'Borrowing Power', icon: TrendingUp, component: BorrowingPower },
  { id: 'repayment', label: 'Repayments', icon: DollarSign, component: RepaymentCalc },
  { id: 'stamp', label: 'Stamp Duty', icon: Home, component: StampDutyCalc },
  { id: 'extra', label: 'Extra Repayments', icon: PiggyBank, component: ExtraRepaymentCalc },
];

const Calculators = () => {
  const [activeTab, setActiveTab] = useState('borrowing');
  const ActiveCalc = tabs.find((t) => t.id === activeTab)?.component;

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Loan Calculators"
        description="Free mortgage calculators — estimate your borrowing power, loan repayments, stamp duty, and savings from extra repayments."
        path="/calculators"
      />
      <Navigation />

      {/* Hero */}
      <section className="pt-16">
        <div className="bg-gradient-to-br from-[#00594C] to-[#003d35] text-white py-16 sm:py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/10 mb-5">
              <Calculator className="w-7 h-7" />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              Loan Calculators
            </h1>
            <p className="mt-3 text-lg text-white/80 max-w-xl mx-auto">
              Free tools to help plan your finances
            </p>
          </div>
        </div>
      </section>

      {/* Calculator card */}
      <section className="max-w-4xl mx-auto px-4 -mt-8 pb-20">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          {/* Tabs */}
          <div className="border-b border-gray-200 overflow-x-auto">
            <nav className="flex min-w-max">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-5 py-4 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                      active
                        ? 'border-[#99DF63] text-[#00594C]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Active calculator */}
          <div className="p-6 sm:p-8">
            {ActiveCalc && <ActiveCalc />}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <div className="bg-gradient-to-br from-[#00594C] to-[#003d35] rounded-2xl p-8 sm:p-10 text-white">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
              Ready to take the next step?
            </h2>
            <p className="mt-2 text-white/80 max-w-lg mx-auto">
              Our expert brokers can provide personalised guidance based on your situation.
            </p>
            <a
              href="/#book-appointment"
              className="inline-flex items-center gap-2 mt-6 px-8 py-3 bg-[#99DF63] text-[#00594C] rounded-full font-bold text-sm hover:brightness-105 active:scale-[0.98] transition"
            >
              Book a consultation
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="mt-8 text-center text-xs text-gray-400 max-w-2xl mx-auto leading-relaxed">
          These calculators provide estimates only and should not be considered financial advice.
          Contact our team for personalised guidance.
        </p>
      </section>

      <Footer />
    </div>
  );
};

export default Calculators;
